import Modal from "react-modal";
import {
  Container,
  ExitBtnContainer,
  HeaderContainer,
  ContentContainer,
  PolicySettingContainer,
} from "./MeetingRoomPolicyModal.styles";
import UI from "@mile-meeting/mile-ui";
import MeetingRoomPolicyItem, { TMeetingRoomSettingForPolicyItem } from "./MeetingRoomPolicyItem/MeetingRoomPolicyItem";
import { MeetingRoomSetting, SettingField } from "./types";
import { TMeetingRoom } from "apis/meetings/meeting-room";
import React, { useEffect } from "react";
import { useToast } from "hook/useToast";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    border: "none",
    background: "transparent",
    transform: "translate(-50%, -50%)",
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};
Modal.setAppElement("#root");

type MeetingRoomPolicyModalProps = {
  modalIsOpen: boolean;
  closeModal: () => void;
  title: string;
  metaData?: SettingField[];
  settingData?: MeetingRoomSetting[];
  onUpdate?: (settingData: MeetingRoomSetting[]) => void;
};

const MeetingRoomPolicyModal: React.FC<MeetingRoomPolicyModalProps> = ({
  modalIsOpen,
  closeModal,
  title,
  metaData = [],
  settingData = [],
  onUpdate = () => {},
}) => {
  const modalRef = React.useRef<HTMLDivElement>(null);
  const itemListRef = React.useRef<HTMLDivElement>(null);
  const [tempSettingData, setTempSettingData] = React.useState<MeetingRoomSetting[]>(settingData);
  const { showToast } = useToast();

  //* 회의실 설정 값의 구조를 정의합니다. (MeetingRoomPolicyItem.tsx에서 사용)
  const mapToPolicyItems = (
    metaData: SettingField[],
    settingData: MeetingRoomSetting[]
  ): {
    meetingRoom: TMeetingRoom;
    items: TMeetingRoomSettingForPolicyItem[];
  }[] => {
    return settingData.map((roomSetting) => {
      const items: TMeetingRoomSettingForPolicyItem[] = metaData.map((field) => {
        const value = roomSetting[field.key];
        return {
          settingField: field,
          value,
          onHandleChange: (newValue: boolean | string | number) => {
            if (field.type === "toggle") {
              setTempSettingData((prev) =>
                prev.map((room) => {
                  if (room.meetingRoom.id === roomSetting.meetingRoom.id) {
                    return { ...room, [field.key]: newValue };
                  }
                  return room;
                })
              );
            }
            if (field.type === "select") {
              //* 노쇼 처리 알림 시간 설정 시, minOptions보다 큰 값으로만 설정 가능
              if (field.key === "noShowProcessingTime") {
                if (field.minOptions && parseInt(`${newValue}`.split("분")[0]) <= field.minOptions) {
                  showToast(
                    "노쇼 경고 알림 시간(현재 설정: " + field.minOptions + "분)보다 큰 값으로만 설정 가능합니다.",
                    "warning"
                  );
                  return;
                }
              }
              setTempSettingData((prev) =>
                prev.map((room) => {
                  if (room.meetingRoom.id === roomSetting.meetingRoom.id) {
                    return { ...room, [field.key]: newValue };
                  }
                  return room;
                })
              );
            }
          },
          disabled: field.disabledWhen?.(roomSetting),
        };
      });
      return {
        meetingRoom: roomSetting.meetingRoom,
        items,
      };
    });
  };
  useEffect(() => {
    setTempSettingData(settingData);
  }, [settingData]);

  const meetingRoomSettingDetail = mapToPolicyItems(metaData, tempSettingData);

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <Container ref={modalRef}>
        <ExitBtnContainer>
          <UI.IconButton.Normal icon="x" size={20} color="var(--alias-color-label-neutral)" onClick={closeModal} />
        </ExitBtnContainer>
        <UI.Typography
          type="heading02"
          fontStyle="bold"
          color="var(--alias-color-label-neutral)"
          style={{
            paddingBottom: "20px",
            borderBottom: "1px solid var(--alias-color-line-normal)",
            width: "100%",
            boxSizing: "border-box",
            height: "48px",
          }}
        >
          {title}
        </UI.Typography>
        <ContentContainer>
          <HeaderContainer>
            <UI.Typography type="body02 normal" fontStyle="bold" color="var(--alias-color-label-alternative)">
              회의실
            </UI.Typography>
            {metaData.map((item) => (
              <UI.Typography
                key={item.key}
                type="body02 normal"
                fontStyle={item.isMainPolicy ? "bold" : "regular"}
                style={{ whiteSpace: "pre-line" }}
                color="var(--alias-color-label-normal)"
              >
                {item.label}
              </UI.Typography>
            ))}
          </HeaderContainer>
          <PolicySettingContainer ref={itemListRef}>
            {meetingRoomSettingDetail.map((room, index) => (
              <MeetingRoomPolicyItem
                parentModalRef={modalRef}
                itemListRef={itemListRef}
                itemIndex={index}
                key={room.meetingRoom.id}
                meetingRoom={room.meetingRoom}
                settingData={room.items}
              />
            ))}
          </PolicySettingContainer>
          <UI.Button
            onClick={() => onUpdate(tempSettingData)}
            title="설정 저장하기"
            size="large"
            style={{
              width: "100%",
              marginTop: "40px",
            }}
          />
        </ContentContainer>
      </Container>
    </Modal>
  );
};

export default MeetingRoomPolicyModal;
